import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, Check, X, Pencil, Download, RefreshCw, ListTodo,
  TrendingUp, AlertTriangle, Users, Flag, LayoutList,
} from "lucide-react";
import { listTasks, addTask, updateTask, deleteTask, downloadXlsx, regenerateManaged } from "@/lib/api";
import { computeResilienceScore } from "@/components/ResilienceScore";

const PRIORITIES = [
  { v: "high", label: "High", cls: "bg-rose-100 text-rose-800", w: 3 },
  { v: "medium", label: "Medium", cls: "bg-amber-100 text-amber-800", w: 2 },
  { v: "low", label: "Low", cls: "bg-slate-100 text-slate-600", w: 1 },
];
const STATUSES = [
  { v: "todo", label: "To do" },
  { v: "in_progress", label: "In progress" },
  { v: "blocked", label: "Blocked" },
  { v: "done", label: "Done" },
];
const STATUS_CLS = {
  todo: "bg-slate-100 text-slate-700",
  in_progress: "bg-sky-100 text-sky-800",
  done: "bg-emerald-100 text-emerald-800",
  blocked: "bg-rose-100 text-rose-800",
};
const P = (v) => PRIORITIES.find((p) => p.v === v) || PRIORITIES[1];
const EMPTY = { title: "", owner: "", due_date: "", priority: "medium", status: "todo", notes: "" };

function isOverdue(t) {
  if (!t.due_date || t.status === "done") return false;
  const d = new Date(t.due_date);
  if (isNaN(d)) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return d < today;
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 min-h-[44px] px-3.5 rounded-full text-sm font-medium border transition-colors ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
      }`}
    >
      {children}
    </button>
  );
}

function TaskRow({ t, onSave, onDelete, onQuickStatus }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(t);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(t), [t]);

  const save = async () => {
    if (!draft.title.trim()) { toast.error("Title is required."); return; }
    setBusy(true);
    try { await onSave(t.id, draft); setEditing(false); } finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className="rounded-lg border border-teal-300 bg-teal-50/40 p-3 space-y-2" data-testid={`task-edit-${t.id}`}>
        <textarea value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} rows={2} placeholder="Task" data-testid={`task-title-input-${t.id}`} className="w-full px-2 py-1.5 rounded-md border border-slate-200 bg-white text-sm" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input value={draft.owner} onChange={(e) => setDraft({ ...draft, owner: e.target.value })} placeholder="Owner" data-testid={`task-owner-input-${t.id}`} className="min-h-[44px] px-2 rounded-md border border-slate-200 bg-white text-sm" />
          <input type="date" value={draft.due_date} onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} data-testid={`task-due-input-${t.id}`} className="min-h-[44px] px-2 rounded-md border border-slate-200 bg-white text-sm" />
          <select value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: e.target.value })} data-testid={`task-priority-input-${t.id}`} className="min-h-[44px] px-2 rounded-md border border-slate-200 bg-white text-sm">
            {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
          <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} data-testid={`task-status-input-${t.id}`} className="min-h-[44px] px-2 rounded-md border border-slate-200 bg-white text-sm">
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </div>
        <textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder="Notes" rows={2} data-testid={`task-notes-input-${t.id}`} className="w-full px-2 py-1.5 rounded-md border border-slate-200 bg-white text-sm" />
        <div className="flex justify-end gap-2">
          <button onClick={() => { setEditing(false); setDraft(t); }} className="inline-flex items-center gap-1 min-h-[44px] px-3 rounded-md border border-slate-200 text-slate-600 text-sm"><X className="h-4 w-4" /> Cancel</button>
          <button onClick={save} disabled={busy} data-testid={`task-save-${t.id}`} className="inline-flex items-center gap-1 min-h-[44px] px-4 rounded-md bg-slate-900 text-white text-sm disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save</button>
        </div>
      </div>
    );
  }

  const pr = P(t.priority);
  const overdue = isOverdue(t);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col sm:flex-row sm:items-start gap-3" data-testid={`task-row-${t.id}`}>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium text-slate-900 break-words whitespace-pre-wrap ${t.status === "done" ? "line-through text-slate-400" : ""}`} data-testid={`task-title-${t.id}`}>{t.title}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]">
          <span className={`px-2 py-0.5 rounded-full font-medium ${pr.cls}`}>{pr.label}</span>
          {overdue && <span className="px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700 inline-flex items-center gap-1" data-testid={`task-overdue-${t.id}`}><AlertTriangle className="h-3 w-3" /> Overdue</span>}
          {t.owner && <span className="text-slate-500">{t.owner}</span>}
          {t.due_date && <span className={overdue ? "text-red-600 font-medium" : "text-slate-500"}>Due {t.due_date}</span>}
          {t.source === "report" && <span className="text-slate-400">· from report</span>}
        </div>
        {t.notes && <p className="mt-1.5 text-xs text-slate-500 break-words whitespace-pre-wrap">{t.notes}</p>}
      </div>
      <div className="flex items-center justify-between sm:justify-end sm:flex-col sm:items-end gap-2 shrink-0">
        <select
          value={t.status}
          onChange={(e) => onQuickStatus(t.id, e.target.value)}
          data-testid={`task-quick-status-${t.id}`}
          className={`min-h-[44px] px-2 rounded-md border-0 text-xs font-medium ${STATUS_CLS[t.status] || STATUS_CLS.todo}`}
        >
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <div className="flex gap-1">
          <button onClick={() => setEditing(true)} data-testid={`task-edit-btn-${t.id}`} className="p-2.5 text-slate-400 hover:text-teal-700"><Pencil className="h-4 w-4" /></button>
          <button onClick={() => onDelete(t.id)} data-testid={`task-delete-${t.id}`} className="p-2.5 text-rose-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}

export default function LivingActionPlan({ assessmentId, readiness }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [groupBy, setGroupBy] = useState("none"); // none | owner | priority

  useEffect(() => {
    listTasks(assessmentId).then(setTasks).catch(() => toast.error("Could not load your action plan")).finally(() => setLoading(false));
  }, [assessmentId]);

  const score = computeResilienceScore(readiness || {});

  const { total, done, pct } = useMemo(() => {
    const total = tasks.length;
    const done = tasks.filter((t) => t.status === "done").length;
    const totalW = tasks.reduce((a, t) => a + P(t.priority).w, 0);
    const doneW = tasks.filter((t) => t.status === "done").reduce((a, t) => a + P(t.priority).w, 0);
    return { total, done, pct: totalW ? Math.round((doneW / totalW) * 100) : 0 };
  }, [tasks]);

  const filtered = useMemo(() => tasks.filter(
    (t) => (statusFilter === "all" || t.status === statusFilter) && (priorityFilter === "all" || t.priority === priorityFilter)
  ), [tasks, statusFilter, priorityFilter]);

  const groups = useMemo(() => {
    if (groupBy === "none") return [{ key: "", label: "", items: filtered }];
    if (groupBy === "owner") {
      const map = {};
      filtered.forEach((t) => { const k = (t.owner || "").trim() || "Unassigned"; (map[k] ||= []).push(t); });
      return Object.keys(map).sort().map((k) => ({ key: k, label: k, items: map[k] }));
    }
    return PRIORITIES.map((p) => ({ key: p.v, label: `${p.label} priority`, items: filtered.filter((t) => t.priority === p.v) })).filter((g) => g.items.length);
  }, [filtered, groupBy]);

  const create = async () => {
    if (!draft.title.trim()) { toast.error("Enter a task title."); return; }
    setSaving(true);
    try { setTasks(await addTask(assessmentId, draft)); setDraft(EMPTY); setAdding(false); toast.success("Action added"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add action"); }
    finally { setSaving(false); }
  };
  const save = async (id, d) => { try { setTasks(await updateTask(assessmentId, id, d)); toast.success("Saved"); } catch { toast.error("Save failed"); } };
  const quickStatus = async (id, status) => {
    const t = tasks.find((x) => x.id === id);
    setTasks((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
    try { setTasks(await updateTask(assessmentId, id, { ...t, status })); } catch { toast.error("Could not update status"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this action?")) return; try { setTasks(await deleteTask(assessmentId, id)); } catch { toast.error("Delete failed"); } };
  const regen = async () => {
    setRegenerating(true);
    try { await regenerateManaged(assessmentId); toast.success("Quarterly refresh started — your report re-scores and surfaces new actions in ~30s."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not refresh"); }
    finally { setRegenerating(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-8" data-testid="living-action-plan">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ListTodo className="h-4 w-4 text-teal-600" />
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Managed Resilience</p>
          </div>
          <h3 className="mt-2 font-display text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">Your Living Action Plan</h3>
          <p className="mt-1.5 text-sm text-slate-600 max-w-xl">Work these actions to build resilience over time. Each quarter your Managed Resilience refresh <span className="font-medium text-slate-700">re-scores your assessment and surfaces newly emerging actions</span> — so the plan stays alive.</p>
        </div>
        <div className="flex flex-col sm:flex-row lg:flex-col gap-2">
          <button onClick={async () => { try { await downloadXlsx(assessmentId); } catch { toast.error("Could not download Excel"); } }} data-testid="download-xlsx-btn" className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors">
            <Download className="h-4 w-4" /> Export to Excel
          </button>
          <button onClick={regen} disabled={regenerating} data-testid="regenerate-report-btn" className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-md border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50">
            {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Quarterly refresh
          </button>
        </div>
      </div>

      {/* Score linkage */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5" data-testid="action-plan-score">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="flex items-center gap-3 shrink-0">
            <TrendingUp className="h-5 w-5 text-teal-600" />
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Resilience score</p>
              <p className="font-display text-4xl font-semibold tabular-nums text-slate-900 leading-none mt-1">{score}<span className="text-base text-slate-400 font-normal"> / 100</span></p>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-medium text-slate-800" data-testid="action-plan-progress-copy">You've closed {done} of {total} actions.</p>
              <span className="text-xs text-slate-500 tabular-nums">{pct}%</span>
            </div>
            <div className="mt-2 h-2.5 w-full rounded-full bg-slate-200 overflow-hidden" data-testid="action-plan-progress-bar">
              <div className="h-full rounded-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-2 text-xs text-slate-500 leading-relaxed">
              Starting score: <span className="font-medium text-slate-700">{score}/100</span>. Completing actions is how your score improves — we keep the <span className="font-medium">official re-score</span> to your quarterly refresh, so this bar tracks the effort you're putting in now.
            </p>
          </div>
        </div>
      </div>

      {/* Filter / segment row */}
      <div className="mt-6" data-testid="action-plan-filters">
        {/* Mobile: compact dropdowns (pills were too clunky on small screens) */}
        <div className="grid grid-cols-3 gap-2 sm:hidden">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">Status</span>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status-select" className="min-h-[44px] px-2 rounded-md border border-slate-200 bg-white text-sm text-slate-700">
              <option value="all">All</option>
              {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">Priority</span>
            <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} data-testid="filter-priority-select" className="min-h-[44px] px-2 rounded-md border border-slate-200 bg-white text-sm text-slate-700">
              <option value="all">All</option>
              {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">Group by</span>
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} data-testid="group-select" className="min-h-[44px] px-2 rounded-md border border-slate-200 bg-white text-sm text-slate-700">
              <option value="none">None</option>
              <option value="owner">Owner</option>
              <option value="priority">Priority</option>
            </select>
          </label>
        </div>

        {/* Desktop: pills */}
        <div className="hidden sm:block space-y-3">
          <div className="flex flex-wrap gap-2">
            <Chip active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testid="filter-status-all">All statuses</Chip>
            {STATUSES.map((s) => (
              <Chip key={s.v} active={statusFilter === s.v} onClick={() => setStatusFilter(s.v)} testid={`filter-status-${s.v}`}>{s.label}</Chip>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Chip active={priorityFilter === "all"} onClick={() => setPriorityFilter("all")} testid="filter-priority-all">All priorities</Chip>
            {PRIORITIES.map((p) => (
              <Chip key={p.v} active={priorityFilter === p.v} onClick={() => setPriorityFilter(p.v)} testid={`filter-priority-${p.v}`}>{p.label}</Chip>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-100">
            <span className="inline-flex items-center min-h-[44px] text-xs uppercase tracking-wide text-slate-400 pr-1">Group by</span>
            <Chip active={groupBy === "none"} onClick={() => setGroupBy("none")} testid="group-none"><LayoutList className="h-4 w-4" /> None</Chip>
            <Chip active={groupBy === "owner"} onClick={() => setGroupBy("owner")} testid="group-owner"><Users className="h-4 w-4" /> Owner</Chip>
            <Chip active={groupBy === "priority"} onClick={() => setGroupBy("priority")} testid="group-priority"><Flag className="h-4 w-4" /> Priority</Chip>
          </div>
        </div>
      </div>

      {/* Add task */}
      <div className="mt-6">
        {adding ? (
          <div className="rounded-lg border border-teal-300 bg-teal-50/40 p-3 space-y-2" data-testid="task-add-form">
            <textarea value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} rows={2} placeholder="What needs to happen?" data-testid="task-new-title" className="w-full px-2 py-1.5 rounded-md border border-slate-200 bg-white text-sm" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input value={draft.owner} onChange={(e) => setDraft({ ...draft, owner: e.target.value })} placeholder="Owner" data-testid="task-new-owner" className="min-h-[44px] px-2 rounded-md border border-slate-200 bg-white text-sm" />
              <input type="date" value={draft.due_date} onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} data-testid="task-new-due" className="min-h-[44px] px-2 rounded-md border border-slate-200 bg-white text-sm" />
              <select value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: e.target.value })} data-testid="task-new-priority" className="min-h-[44px] px-2 rounded-md border border-slate-200 bg-white text-sm">
                {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
              <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} data-testid="task-new-status" className="min-h-[44px] px-2 rounded-md border border-slate-200 bg-white text-sm">
                {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setAdding(false); setDraft(EMPTY); }} className="inline-flex items-center gap-1 min-h-[44px] px-3 rounded-md border border-slate-200 text-slate-600 text-sm"><X className="h-4 w-4" /> Cancel</button>
              <button onClick={create} disabled={saving} data-testid="task-create-btn" className="inline-flex items-center gap-1 min-h-[44px] px-4 rounded-md bg-teal-600 text-white text-sm disabled:opacity-50">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add action</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} data-testid="task-add-btn" className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-md border border-dashed border-teal-400 text-teal-700 text-sm font-medium hover:bg-teal-50 transition-colors">
            <Plus className="h-4 w-4" /> Add an action
          </button>
        )}
      </div>

      {/* Task list */}
      <div className="mt-6 space-y-5">
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        ) : filtered.length ? (
          groups.map((g) => (
            <div key={g.key || "flat"} className="space-y-2">
              {g.label && <p className="text-xs font-semibold uppercase tracking-wide text-slate-400" data-testid={`task-group-${g.key}`}>{g.label} · {g.items.length}</p>}
              {g.items.map((t) => <TaskRow key={t.id} t={t} onSave={save} onDelete={remove} onQuickStatus={quickStatus} />)}
            </div>
          ))
        ) : (
          <p className="text-sm text-slate-500">{tasks.length ? "No actions match these filters." : "No actions yet — add your first one above."}</p>
        )}
      </div>
    </div>
  );
}
